import { useState, useEffect } from 'react';
import { useTheme } from 'next-themes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Eye, Type, Zap, Keyboard, Volume2, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

const accessibilitySchema = z.object({
  highContrast: z.boolean(),
  textSize: z.number().min(12).max(24),
  reducedMotion: z.boolean(),
  screenReaderMode: z.boolean(),
  keyboardShortcuts: z.boolean(),
  focusIndicators: z.enum(['default', 'enhanced', 'high-visibility']),
  colorMode: z.enum(['auto', 'light', 'dark', 'high-contrast']),
});

type AccessibilitySettings = z.infer<typeof accessibilitySchema>;

const defaultSettings: AccessibilitySettings = {
  highContrast: false,
  textSize: 16,
  reducedMotion: false,
  screenReaderMode: false,
  keyboardShortcuts: true,
  focusIndicators: 'default',
  colorMode: 'auto',
};

export default function AccessibilitySettings() {
  const { theme, setTheme } = useTheme();
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    applySettings(settings);
  }, [settings]);

  const loadSettings = () => {
    try {
      const stored = localStorage.getItem('accessibility-settings');
      if (stored) {
        const parsed = JSON.parse(stored);
        const validated = accessibilitySchema.parse(parsed);
        setSettings(validated);
      }
    } catch (error) {
      console.error('Error loading accessibility settings:', error);
      toast.error('Failed to load settings, using defaults');
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const validated = accessibilitySchema.parse(settings);
      localStorage.setItem('accessibility-settings', JSON.stringify(validated));
      applySettings(validated);
      toast.success('Accessibility settings saved');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
    localStorage.removeItem('accessibility-settings');
    toast.success('Settings reset to defaults');
  };

  const applySettings = (s: AccessibilitySettings) => {
    const root = document.documentElement;
    
    // Apply text size
    root.style.fontSize = `${s.textSize}px`;
    
    // Apply high contrast
    if (s.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
    
    // Apply reduced motion
    if (s.reducedMotion) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }
    
    // Apply focus indicators
    root.setAttribute('data-focus-style', s.focusIndicators);
    
    // Apply color mode using next-themes
    if (s.colorMode === 'high-contrast') {
      root.classList.add('high-contrast-theme');
      setTheme('dark');
    } else {
      root.classList.remove('high-contrast-theme');
      // Map accessibility colorMode to theme
      if (s.colorMode === 'auto') {
        setTheme('system');
      } else {
        setTheme(s.colorMode as 'light' | 'dark');
      }
    }

    // Screen reader announcements
    if (s.screenReaderMode) {
      root.setAttribute('aria-live', 'polite');
    }
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Accessibility Settings</h1>
        <p className="text-muted-foreground mt-2">
          Customize your experience to meet your accessibility needs
        </p>
      </div>

      <div className="space-y-6">
        {/* Visual Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Visual Settings
            </CardTitle>
            <CardDescription>
              Adjust visual elements for better readability
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="high-contrast">High Contrast Mode</Label>
                <p className="text-sm text-muted-foreground">
                  Increase contrast for better visibility
                </p>
              </div>
              <Switch
                id="high-contrast"
                checked={settings.highContrast}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, highContrast: checked }))}
                aria-label="Toggle high contrast mode"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="text-size">Text Size: {settings.textSize}px</Label>
              <Slider
                id="text-size"
                min={12}
                max={24}
                step={1}
                value={[settings.textSize]}
                onValueChange={(value) => setSettings(prev => ({ ...prev, textSize: value[0] }))}
                className="w-full"
                aria-label="Adjust text size"
              />
              <p className="text-sm text-muted-foreground">
                Default is 16px. Adjust to your comfort level.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="color-mode">Color Mode</Label>
              <Select
                value={settings.colorMode}
                onValueChange={(value: any) => setSettings(prev => ({ ...prev, colorMode: value }))}
              >
                <SelectTrigger id="color-mode" aria-label="Select color mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (System)</SelectItem>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                  <SelectItem value="high-contrast">High Contrast</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Motion & Animation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Motion & Animation
            </CardTitle>
            <CardDescription>
              Control animations and transitions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="reduced-motion">Reduced Motion</Label>
                <p className="text-sm text-muted-foreground">
                  Minimize animations and transitions
                </p>
              </div>
              <Switch
                id="reduced-motion"
                checked={settings.reducedMotion}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, reducedMotion: checked }))}
                aria-label="Toggle reduced motion"
              />
            </div>
          </CardContent>
        </Card>

        {/* Keyboard & Navigation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Keyboard className="h-5 w-5" />
              Keyboard & Navigation
            </CardTitle>
            <CardDescription>
              Customize keyboard navigation and focus indicators
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="keyboard-shortcuts">Keyboard Shortcuts</Label>
                <p className="text-sm text-muted-foreground">
                  Enable keyboard shortcuts for quick navigation
                </p>
              </div>
              <Switch
                id="keyboard-shortcuts"
                checked={settings.keyboardShortcuts}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, keyboardShortcuts: checked }))}
                aria-label="Toggle keyboard shortcuts"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="focus-indicators">Focus Indicators</Label>
              <Select
                value={settings.focusIndicators}
                onValueChange={(value: any) => setSettings(prev => ({ ...prev, focusIndicators: value }))}
              >
                <SelectTrigger id="focus-indicators" aria-label="Select focus indicator style">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="enhanced">Enhanced</SelectItem>
                  <SelectItem value="high-visibility">High Visibility</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Choose how focus indicators appear when navigating with keyboard
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Screen Reader */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Volume2 className="h-5 w-5" />
              Screen Reader
            </CardTitle>
            <CardDescription>
              Optimize for screen reader usage
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="screen-reader">Screen Reader Mode</Label>
                <p className="text-sm text-muted-foreground">
                  Enhance compatibility with screen readers
                </p>
              </div>
              <Switch
                id="screen-reader"
                checked={settings.screenReaderMode}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, screenReaderMode: checked }))}
                aria-label="Toggle screen reader mode"
              />
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <Button
            onClick={saveSettings}
            disabled={saving}
            className="flex-1 transition-all hover:scale-105"
            aria-label="Save accessibility settings"
          >
            {saving ? (
              <>
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={resetSettings}
            className="transition-all hover:scale-105"
            aria-label="Reset to default settings"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
        </div>

        {/* Information Card */}
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle className="text-base">About These Settings</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <p>
              These accessibility settings are designed to meet ADA compliance standards and provide accommodations for:
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Users with visual impairments (high contrast, text sizing)</li>
              <li>Users with motion sensitivity (reduced motion)</li>
              <li>Keyboard-only navigation</li>
              <li>Screen reader users</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              Settings are saved locally on this device and will persist across sessions.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
